<template>
  <div class="employee-management">
    <div class="lvyun-profile-banner">
      <h2>员工与统一账号</h2>
      <p>后台员工与小程序用户共用一套账号体系；角色分为普通账号、管理员、超级管理员。</p>
    </div>
    <el-card class="employee-card" shadow="never">
      <template slot="header">
        <div class="card-header">
          <div class="card-title">员工管理</div>
          <el-button
            type="primary"
            icon="el-icon-plus"
            class="btn-primary"
            @click="showAddDialog"
          >
            添加员工
          </el-button>
        </div>
      </template>
      
      <el-table
        :data="employees"
        style="width: 100%"
        stripe
        class="employee-table"
      >
        <el-table-column label="员工ID" width="140">
          <template slot-scope="scope">
            {{ scope.row.custom_employee_id || scope.row.id }}
          </template>
        </el-table-column>
        <el-table-column
          prop="name"
          label="员工姓名"
          width="120"
          class-name="employee-name-cell"
        ></el-table-column>
        <el-table-column prop="username" label="账号" width="150"></el-table-column>
        <el-table-column prop="phone" label="联系电话" width="150"></el-table-column>
        <el-table-column label="入职时间" width="120">
          <template slot-scope="scope">
            {{ scope.row.hire_date || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="role" label="角色" width="140">
          <template slot-scope="scope">
            <span
              class="role-tag"
              :class="getRoleClass(scope.row.role)"
            >
              {{ scope.row.role }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="employment_status" label="在职状态" width="110">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.employment_status === '在职' ? 'success' : 'info'">
              {{ scope.row.employment_status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="权限" min-width="300">
          <template slot-scope="scope">
            <span
              class="permission-tag"
              v-for="permission in scope.row.permissions" 
              :key="permission"
            >
              {{ permission }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="健康证时间" width="140">
          <template slot-scope="scope">
            {{ scope.row.health_certificate_expiry_date || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="健康证状态" width="110">
          <template slot-scope="scope">
            <el-tag size="mini" :type="healthStatusType(scope.row.health_certificate_status)">
              {{ scope.row.health_certificate_status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间">
          <template slot-scope="scope">{{ $formatDateTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="260">
          <template slot-scope="scope">
            <el-button size="small" class="btn-secondary" @click="editEmployee(scope.row)">编辑</el-button>
            <el-button
              v-if="scope.row.role === '管理员'"
              size="small"
              class="btn-secondary"
              @click="setPermissions(scope.row)"
            >设置权限</el-button>
            <el-button
              v-if="canAdminDeleteSession"
              size="small"
              class="btn-secondary danger"
              @click="deleteEmployee(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 添加/编辑员工对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="employeeForm" :rules="rules" ref="employeeForm" label-width="100px">
        <el-form-item label="员工姓名" prop="name">
          <el-input v-model="employeeForm.name" placeholder="请输入员工姓名"></el-input>
        </el-form-item>
        <el-form-item label="自定义员工ID" prop="custom_employee_id">
          <el-input v-model="employeeForm.custom_employee_id" placeholder="可选，如 A001"></el-input>
        </el-form-item>
        <el-form-item label="账号" prop="username">
          <el-input v-model="employeeForm.username" placeholder="请输入账号"></el-input>
        </el-form-item>
        <el-form-item label="密码" prop="password" v-if="!isEdit">
          <el-input v-model="employeeForm.password" type="password" placeholder="请输入密码"></el-input>
        </el-form-item>
        <el-form-item label="联系电话" prop="phone">
          <el-input v-model="employeeForm.phone" placeholder="请输入联系电话"></el-input>
        </el-form-item>
        <el-form-item label="入职时间" prop="hire_date">
          <el-date-picker
            v-model="employeeForm.hire_date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="请选择入职日期"
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="健康证发证日" prop="health_certificate_issue_date">
          <el-date-picker
            v-model="employeeForm.health_certificate_issue_date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="请选择发证日期"
            style="width: 100%;"
          />
          <div class="form-perm-hint">到期时间自动按「发证日 +1年 -1天」计算。</div>
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="employeeForm.role" placeholder="请选择角色" style="width: 100%;">
            <el-option
              v-for="opt in roleSelectOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="在职状态" prop="employment_status">
          <el-select v-model="employeeForm.employment_status" placeholder="请选择在职状态" style="width: 100%;">
            <el-option label="在职" value="在职" />
            <el-option label="离职" value="离职" />
          </el-select>
        </el-form-item>
        <el-form-item
          v-if="isEdit && isSuperAdminSession"
          label="新登录密码"
          prop="newPassword"
        >
          <el-input
            v-model="employeeForm.newPassword"
            type="password"
            placeholder="不修改请留空；仅超级管理员可改密"
            show-password
            autocomplete="new-password"
          />
        </el-form-item>
        <el-form-item label="权限" v-if="canEditPermissions">
          <el-checkbox-group v-model="employeeForm.permissions">
            <el-checkbox label="订单管理"></el-checkbox>
            <el-checkbox label="商品管理"></el-checkbox>
            <el-checkbox label="客户管理"></el-checkbox>
            <el-checkbox label="兑换券管理"></el-checkbox>
            <el-checkbox label="随心订管理"></el-checkbox>
            <el-checkbox label="员工管理"></el-checkbox>
            <el-checkbox label="骑手管理"></el-checkbox>
            <el-checkbox label="数据统计"></el-checkbox>
          </el-checkbox-group>
          <div class="form-perm-hint" v-if="canEditPermissions">管理员可勾选功能权限；超级管理员默认全部权限</div>
          <div class="form-perm-hint" v-else-if="employeeForm.role === '超级管理员'">超级管理员拥有全部后台权限，并同步至统一账号</div>
          <div class="form-perm-hint" v-else>普通账号仅用于小程序，无后台管理权限</div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEmployee">确定</el-button>
      </span>
    </el-dialog>
    
    <!-- 设置权限对话框 -->
    <el-dialog title="设置权限" :visible.sync="permissionDialogVisible" width="500px">
      <el-form label-width="100px">
        <el-form-item label="员工姓名">
          <span>{{ currentEmployee.name }}</span>
        </el-form-item>
        <el-form-item label="选择权限">
          <el-checkbox-group v-model="currentEmployee.permissions">
            <el-checkbox label="订单管理"></el-checkbox>
            <el-checkbox label="商品管理"></el-checkbox>
            <el-checkbox label="客户管理"></el-checkbox>
            <el-checkbox label="兑换券管理"></el-checkbox>
            <el-checkbox label="随心订管理"></el-checkbox>
            <el-checkbox label="员工管理"></el-checkbox>
            <el-checkbox label="骑手管理"></el-checkbox>
            <el-checkbox label="数据统计"></el-checkbox>
          </el-checkbox-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="permissionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="savePermissions">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { isSuperAdminUser, canAdminDelete } from '../utils/auth'
import { formatDateTime } from '../utils/dateTimeUtils'

export default {
  name: 'EmployeeManagement',
  data() {
    return {
      employees: [],
      dialogVisible: false,
      permissionDialogVisible: false,
      dialogTitle: '添加员工',
      isEdit: false,
      currentEmployee: {},
      employeeForm: {
        id: '',
        custom_employee_id: '',
        name: '',
        username: '',
        password: '',
        newPassword: '',
        phone: '',
        hire_date: '',
        health_certificate_issue_date: '',
        role: '',
        employment_status: '在职',
        permissions: []
      },
      rules: {
        name: [
          { required: true, message: '请输入员工姓名', trigger: 'blur' }
        ],
        username: [
          { required: true, message: '请输入账号', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        role: [
          { required: true, message: '请选择角色', trigger: 'change' }
        ],
        employment_status: [{ required: true, message: '请选择在职状态', trigger: 'change' }],
        custom_employee_id: [
          {
            validator: (_r, v, cb) => {
              const s = String(v || '').trim()
              if (!s) return cb()
              if (s.length > 64) return cb(new Error('自定义员工ID最长 64 字符'))
              cb()
            },
            trigger: 'blur'
          }
        ],
        newPassword: [
          {
            validator: (_r, v, cb) => {
              const s = String(v || '').trim()
              if (!s) return cb()
              if (s.length < 6) return cb(new Error('密码至少 6 位'))
              cb()
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  computed: {
    isSuperAdminSession() {
      return isSuperAdminUser()
    },
    canAdminDeleteSession() {
      return canAdminDelete()
    },
    roleSelectOptions() {
      return [
        { label: '超级管理员', value: '超级管理员' },
        { label: '管理员', value: '管理员' },
        { label: '普通账号', value: '普通账号' },
        { label: '员工（兼容）', value: '员工' },
        { label: '收银员（兼容）', value: '收银员' }
      ]
    },
    canEditPermissions() {
      const role = String(this.employeeForm.role || '')
      return role === '管理员'
    }
  },
  mounted() {
    this.getEmployees()
  },
  methods: {
    getEmployees() {
      const format = (d) => {
        const s = formatDateTime(d)
        return s === '—' ? '' : s
      }

      this.$api
        .get('/admin/employees')
        .then((resp) => {
          if (!resp || resp.code !== 200) {
            this.employees = []
            return
          }
          const list = resp.data || []
          if (
            list.length === 0 &&
            resp.message &&
            /db:sync|表缺失|eating_employees|无法连接/i.test(String(resp.message))
          ) {
            this.$message.warning(String(resp.message).slice(0, 200))
          }
          const sortIdOf = (row) => String(row.custom_employee_id || row.id || '')
          const parseNumSuffix = (s) => {
            const m = String(s || '').match(/(\d+)$/)
            return m ? Number(m[1]) : Number.NaN
          }
          this.employees = list
            .map((e) => ({
              id: e._id != null ? e._id : e.id,
              custom_employee_id: e.custom_employee_id || '',
              name: e.name,
              username: e.username,
              phone: e.phone,
              role: e.role,
              hire_date: e.hire_date || '',
              employment_status: e.employment_status || '在职',
              permissions: e.permissions || [],
              health_certificate_issue_date: e.health_certificate_issue_date || '',
              health_certificate_expiry_date:
                e.health_certificate_expiry_date || this.calcHealthCertExpiryDate(e.health_certificate_issue_date),
              health_certificate_status: this.healthStatusByIssueDate(e.health_certificate_issue_date),
              createdAt: format(e.createdAt || e.created_at)
            }))
            .sort((a, b) => {
              const ida = sortIdOf(a)
              const idb = sortIdOf(b)
              const na = parseNumSuffix(ida)
              const nb = parseNumSuffix(idb)
              const hasNa = Number.isFinite(na)
              const hasNb = Number.isFinite(nb)
              if (hasNa && hasNb && na !== nb) return na - nb
              if (hasNa !== hasNb) return hasNa ? -1 : 1
              return ida.localeCompare(idb, 'zh-CN', { numeric: true, sensitivity: 'base' })
            })
        })
        .catch((err) => {
          this.employees = []
          const msg =
            (err && err.response && err.response.data && err.response.data.message) ||
            (err && err._networkHint) ||
            '获取员工列表失败'
          this.$message.error(msg)
        })
    },
    showAddDialog() {
      this.dialogTitle = '添加员工';
      this.isEdit = false;
      this.employeeForm = {
        id: '',
        custom_employee_id: '',
        name: '',
        username: '',
        password: '',
        newPassword: '',
        phone: '',
        hire_date: '',
        health_certificate_issue_date: '',
        role: '',
        employment_status: '在职',
        permissions: []
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.employeeForm && this.$refs.employeeForm.clearValidate();
      });
    },
    editEmployee(employee) {
      this.dialogTitle = '编辑员工';
      this.isEdit = true;
      this.employeeForm = {
        ...employee,
        custom_employee_id: employee.custom_employee_id || '',
        hire_date: employee.hire_date || '',
        health_certificate_issue_date: employee.health_certificate_issue_date || '',
        employment_status: employee.employment_status || '在职',
        newPassword: '',
        permissions: Array.isArray(employee.permissions) ? [...employee.permissions] : []
      };
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.$refs.employeeForm && this.$refs.employeeForm.clearValidate();
      });
    },
    submitEmployee() {
      this.$refs.employeeForm.validate((valid) => {
        if (valid) {
          if (this.isEdit) {
            const id = this.employeeForm.id
            const payload = {
              name: this.employeeForm.name,
              custom_employee_id: String(this.employeeForm.custom_employee_id || '').trim(),
              username: this.employeeForm.username,
              phone: this.employeeForm.phone,
              hire_date: this.employeeForm.hire_date || '',
              health_certificate_issue_date: this.employeeForm.health_certificate_issue_date || '',
              role: this.employeeForm.role,
              employment_status: this.employeeForm.employment_status || '在职',
              permissions: this.employeeForm.permissions || []
            }
            const np = String(this.employeeForm.newPassword || '').trim()
            if (this.isSuperAdminSession && np) {
              payload.password = np
            }
            this.$api
              .put(`/admin/employees/${id}`, payload)
              .then((resp) => {
                if (resp && resp.code === 200) {
                  this.$message.success('员工信息更新成功！')
                  this.dialogVisible = false
                  this.getEmployees()
                } else {
                  this.$message.error((resp && resp.message) || '员工更新失败')
                }
              })
              .catch((err) => {
                const msg =
                  (err && err.response && err.response.data && err.response.data.message) ||
                  (err && err._networkHint) ||
                  '员工更新失败'
                this.$message.error(msg)
              })
          } else {
            const payload = {
              name: this.employeeForm.name,
              custom_employee_id: String(this.employeeForm.custom_employee_id || '').trim(),
              username: this.employeeForm.username,
              password: this.employeeForm.password,
              phone: this.employeeForm.phone,
              hire_date: this.employeeForm.hire_date || '',
              health_certificate_issue_date: this.employeeForm.health_certificate_issue_date || '',
              role: this.employeeForm.role,
              employment_status: this.employeeForm.employment_status || '在职',
              permissions: this.employeeForm.permissions || []
            }
            this.$api
              .post('/admin/employees', payload)
              .then((resp) => {
                if (resp && resp.code === 201) {
                  this.$message.success('员工添加成功！')
                  this.dialogVisible = false
                  this.getEmployees()
                } else {
                  this.$message.error((resp && resp.message) || '员工添加失败（请确认已执行建表 db:sync）')
                }
              })
              .catch((err) => {
                const msg =
                  (err && err.response && err.response.data && err.response.data.message) ||
                  (err && err._networkHint) ||
                  '员工添加失败'
                this.$message.error(msg)
              })
          }
        }
      });
    },
    setPermissions(employee) {
      this.currentEmployee = { ...employee };
      this.permissionDialogVisible = true;
    },
    savePermissions() {
      const id = this.currentEmployee.id
      const payload = {
        permissions: this.currentEmployee.permissions || []
      }
      this.$api
        .put(`/admin/employees/${id}`, payload)
        .then((resp) => {
          if (resp && resp.code === 200) {
            this.$message.success('权限设置成功！该员工需重新登录后新权限方可在接口层生效。')
            this.permissionDialogVisible = false
            this.getEmployees()
          } else {
            this.$message.error((resp && resp.message) || '权限设置失败')
          }
        })
        .catch((err) => {
          const msg =
            (err && err.response && err.response.data && err.response.data.message) ||
            '权限设置失败'
          this.$message.error(msg)
        })
    },
    deleteEmployee(employee) {
      this.$confirm(`确定要删除员工"${employee.name}"吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$api
          .delete(`/admin/employees/${employee.id}`)
          .then((resp) => {
            if (resp && resp.code === 200) {
              this.$message.success('员工删除成功！')
              this.getEmployees()
            } else {
              this.$message.error((resp && resp.message) || '员工删除失败')
            }
          })
          .catch((err) => {
            const msg =
              (err && err.response && err.response.data && err.response.data.message) ||
              '员工删除失败'
            this.$message.error(msg)
          })
      }).catch(() => {
      });
    },
    calcHealthCertExpiryDate(issueDate) {
      const s = String(issueDate || '').trim()
      if (!s) return ''
      const d = new Date(`${s}T00:00:00+08:00`)
      if (Number.isNaN(d.getTime())) return ''
      d.setFullYear(d.getFullYear() + 1)
      d.setDate(d.getDate() - 1)
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    },
    healthStatusByIssueDate(issueDate) {
      const expiry = this.calcHealthCertExpiryDate(issueDate)
      if (!expiry) return '过期'
      const now = new Date()
      const p = (n) => String(n).padStart(2, '0')
      const today = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`
      return expiry >= today ? '正常' : '过期'
    },
    healthStatusType(status) {
      return status === '正常' ? 'success' : 'danger'
    },
    getRoleClass(role) {
      switch (role) {
        case '超级管理员': return 'role-super';
        case '管理员': return 'role-admin';
        case '普通账号': return 'role-normal';
        case '员工': return 'role-staff';
        case '收银员': return 'role-cashier';
        default: return 'role-default';
      }
    }
  }
}
</script>

<style>
.employee-management {
  padding: 24px;
  background: #f5f7fa;
}

.employee-card {
  border-radius: 12px;
  box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.06);
  border: none;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}

.btn-primary {
  border-radius: 12px;
  padding: 8px 16px;
  font-weight: 600;
}

.btn-secondary {
  border-radius: 12px;
}

.employee-table ::v-deep(.el-table__header-wrapper th) {
  background: #f7f8fa;
  color: #1d2129;
  font-weight: 600;
  padding-top: 14px;
  padding-bottom: 14px;
}

.employee-table ::v-deep(.el-table__header-wrapper thead tr) {
  background: #f7f8fa;
}

.employee-table ::v-deep(.el-table__body-wrapper td) {
  padding-top: 16px;
  padding-bottom: 16px;
}

.employee-table ::v-deep(.el-table__body-wrapper tr:nth-child(2n)) {
  background: #fafafa;
}

.employee-table ::v-deep(.el-table__body-wrapper tr:hover > td) {
  background-color: #f2f3f5 !important;
  transition: background-color 0.2s ease;
}

.employee-table ::v-deep(.cell) {
  white-space: nowrap;
}

.employee-table ::v-deep(td.employee-name-cell .cell) {
  white-space: nowrap !important;
  word-break: normal !important;
}

.employee-table ::v-deep(.el-table__inner-wrapper) {
  border-bottom: 1px solid #e5e6eb;
}

.role-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.role-super {
  background: rgba(22, 93, 255, 0.12);
  color: #1546d4;
  border: 1px solid rgba(22, 93, 255, 0.35);
  border-radius: 999px;
}

.role-admin {
  background: rgba(46, 204, 113, 0.15);
  color: #1e8f4a;
  border: 1px solid rgba(46, 204, 113, 0.35);
  border-radius: 999px;
}

.role-normal {
  background: #eef2f7;
  color: #606266;
  border: 1px solid #dcdfe6;
  border-radius: 999px;
}

.role-staff {
  background: #f5f5f5;
  color: #333333;
  border: 1px solid #e5e5e5;
  border-radius: 999px;
}

.role-cashier {
  background: rgba(46, 204, 113, 0.12);
  color: #1e8f4a;
  border: 1px solid rgba(46, 204, 113, 0.3);
  border-radius: 999px;
}

.role-default {
  background: #f5f5f5;
  color: #555555;
  border-radius: 999px;
}

.form-perm-hint {
  font-size: 12px;
  color: #86909c;
  margin-top: 6px;
  line-height: 1.4;
}

.permission-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  margin-right: 6px;
  margin-bottom: 6px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  background: rgba(43, 182, 115, 0.12);
  color: #047857;
}
</style>
